import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, Eye, Gavel, Heart } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type LeadSegment = "lavt_bud" | "overbudt" | "favoritt" | "sett";
type CustomerType = "browser" | "byder" | "vinner" | "sovende";

type Lead = {
  organizationId: string;
  organizationName: string;
  phone: string | null;
  isBot: boolean;
  segment: LeadSegment;
  maxBidAmount: number | null;
  lastActivityAt: string | null;
};
type ActivityAuction = {
  id: string;
  title: string;
  currentBid: number;
  bidCount: number;
  endsAt: string | null;
  leadCounts: Record<LeadSegment, number>;
  leads: Lead[];
};
type Customer = {
  organizationId: string;
  organizationName: string;
  phone: string | null;
  isBot: boolean;
  views30d: number;
  favorites: number;
  bids: number;
  auctions: number;
  wins: number;
  lastActiveAt: string | null;
  type: CustomerType;
};

type CarActivity = {
  auctionId: string;
  auctionTitle: string;
  isLive: boolean;
  views: number;
  favorited: boolean;
  bidCount: number;
  maxBid: number | null;
  lastAt: string;
};

const formatNOK = (n: number) =>
  new Intl.NumberFormat("nb-NO", { style: "currency", currency: "NOK", maximumFractionDigits: 0 }).format(n);

function BotBadge() {
  return (
    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700 align-middle">
      Bot
    </span>
  );
}

function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${Math.max(mins, 0)}m siden`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}t siden`;
  const days = Math.floor(hours / 24);
  return `${days}d siden`;
}

const SEGMENT_META: Record<LeadSegment, { label: string; variant: "destructive" | "warning" | "accent" | "muted" }> = {
  lavt_bud: { label: "Lavt bud", variant: "destructive" },
  overbudt: { label: "Overbudt", variant: "warning" },
  favoritt: { label: "Favoritt", variant: "accent" },
  sett: { label: "Sett", variant: "muted" },
};

const CUSTOMER_META: Record<CustomerType, { label: string; variant: "accent" | "success" | "default" | "muted" }> = {
  browser: { label: "Browser", variant: "accent" },
  byder: { label: "Byder", variant: "success" },
  vinner: { label: "Vinner", variant: "default" },
  sovende: { label: "Sovende", variant: "muted" },
};

type AuctionScope = "live" | "ended";

function BilerTab({ hideBots }: { hideBots: boolean }) {
  const [auctions, setAuctions] = useState<ActivityAuction[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const [scope, setScope] = useState<AuctionScope>("live");
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/admin/activity/auctions?scope=${scope}`, { credentials: "include" });
        if (!res.ok) throw new Error();
        const data = (await res.json()) as { auctions: ActivityAuction[] };
        if (!cancelled) setAuctions(data.auctions);
      } catch {
        if (!cancelled) setAuctions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [scope]);

  const toggle = (
    <div className="inline-flex rounded-lg bg-muted p-1 text-sm">
      {(["live", "ended"] as AuctionScope[]).map((s) => (
        <button
          key={s}
          onClick={() => { setScope(s); setOpenId(null); }}
          className={`rounded-md px-3 py-1 font-medium transition-colors ${scope === s ? "bg-background shadow-sm text-foreground" : "text-muted-foreground"}`}
        >
          {s === "live" ? "Aktive" : "Avsluttede"}
        </button>
      ))}
    </div>
  );

  const body = loading ? (
    <div className="py-10 text-center text-sm text-muted-foreground">Laster …</div>
  ) : !auctions.length ? (
    <div className="py-10 text-center text-sm text-muted-foreground">
      {scope === "live" ? "Ingen aktive auksjoner." : "Ingen avsluttede auksjoner."}
    </div>
  ) : (
    <div className="space-y-3">
      {auctions.map((a) => {
        const leads = hideBots ? a.leads.filter((l) => !l.isBot) : a.leads;
        const totalLeads = leads.length;
        const isOpen = openId === a.id;
        return (
          <Card key={a.id}>
            <CardHeader
              className="cursor-pointer flex-row items-center justify-between gap-4"
              onClick={() => setOpenId(isOpen ? null : a.id)}
            >
              <div>
                <CardTitle className="text-base">{a.title}</CardTitle>
                <div className="mt-1 text-sm text-muted-foreground">
                  {formatNOK(a.currentBid)} · {a.bidCount} bud · {scope === "live" ? "slutter" : "avsluttet"} {relativeTime(a.endsAt)}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="accent">{totalLeads} leads</Badge>
              </div>
            </CardHeader>
            {isOpen && (
              <CardContent>
                {totalLeads === 0 ? (
                  <div className="py-4 text-sm text-muted-foreground">Ingen interessenter enda.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase text-muted-foreground">
                          <th className="py-2 pr-4">Kunde</th>
                          <th className="py-2 pr-4">Telefon</th>
                          <th className="py-2 pr-4">Status</th>
                          <th className="py-2 pr-4">Deres bud</th>
                          <th className="py-2 pr-4">Sist aktiv</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leads.map((l) => {
                          const meta = SEGMENT_META[l.segment];
                          return (
                            <tr
                              key={l.organizationId}
                              className="cursor-pointer border-t border-border hover:bg-muted/40"
                              onClick={() => navigate(`/organisasjoner/${l.organizationId}`)}
                            >
                              <td className="py-2 pr-4 font-medium">{l.organizationName}{l.isBot && <BotBadge />}</td>
                              <td className="py-2 pr-4">{l.phone ?? "—"}</td>
                              <td className="py-2 pr-4"><Badge variant={meta.variant}>{meta.label}</Badge></td>
                              <td className="py-2 pr-4">{l.maxBidAmount != null ? formatNOK(l.maxBidAmount) : "—"}</td>
                              <td className="py-2 pr-4 text-muted-foreground">{relativeTime(l.lastActivityAt)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-4">
      {toggle}
      {body}
    </div>
  );
}

type SortKey = "views30d" | "favorites" | "bids" | "auctions" | "wins" | "lastActiveAt";

type CustomerFeed = { loading: boolean; cars: CarActivity[] };

function CustomerActivityFeed({ feed }: { feed: CustomerFeed | undefined }) {
  const navigate = useNavigate();
  if (!feed || feed.loading) {
    return <div className="py-4 text-sm text-muted-foreground">Laster aktivitet …</div>;
  }
  if (!feed.cars.length) {
    return <div className="py-4 text-sm text-muted-foreground">Ingen registrert aktivitet.</div>;
  }
  return (
    <div className="divide-y divide-border">
      {feed.cars.map((car) => (
        <div
          key={car.auctionId}
          className="cursor-pointer py-2.5 hover:bg-muted/40"
          onClick={() => navigate(`/auksjoner/${car.auctionId}`)}
        >
          <div className="flex items-center gap-2">
            <span className="truncate font-medium">{car.auctionTitle}</span>
            {car.isLive && (
              <span className="shrink-0 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
                Live
              </span>
            )}
            <span className="ml-auto shrink-0 text-xs text-muted-foreground">{relativeTime(car.lastAt)}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            {car.bidCount > 0 && (
              <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                <Gavel className="h-3.5 w-3.5 text-accent" />
                {car.maxBid != null ? formatNOK(car.maxBid) : "Bud"}
                {car.bidCount > 1 && <span className="font-normal text-muted-foreground">×{car.bidCount}</span>}
              </span>
            )}
            {car.favorited && (
              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                <Heart className="h-3.5 w-3.5 text-rose-500" />
                Favoritt
              </span>
            )}
            {car.views > 0 && (
              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                <Eye className="h-3.5 w-3.5" />
                {car.views} {car.views === 1 ? "visning" : "visninger"}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function KunderTab({ hideBots }: { hideBots: boolean }) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>("views30d");
  const [openId, setOpenId] = useState<string | null>(null);
  const [feeds, setFeeds] = useState<Record<string, CustomerFeed>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/admin/activity/customers`, { credentials: "include" });
        if (!res.ok) throw new Error();
        const data = (await res.json()) as { customers: Customer[] };
        if (!cancelled) setCustomers(data.customers);
      } catch {
        if (!cancelled) setCustomers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const openCustomer = (orgId: string) => {
    if (openId === orgId) { setOpenId(null); return; }
    setOpenId(orgId);
    if (feeds[orgId]) return; // already cached
    setFeeds((f) => ({ ...f, [orgId]: { loading: true, cars: [] } }));
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/activity/customers/${orgId}`, { credentials: "include" });
        if (!res.ok) throw new Error();
        const data = (await res.json()) as { cars: CarActivity[] };
        setFeeds((f) => ({ ...f, [orgId]: { loading: false, cars: data.cars } }));
      } catch {
        setFeeds((f) => ({ ...f, [orgId]: { loading: false, cars: [] } }));
      }
    })();
  };

  const visible = hideBots ? customers.filter((c) => !c.isBot) : customers;
  const sorted = [...visible].sort((a, b) => {
    if (sortKey === "lastActiveAt") return (b.lastActiveAt ?? "").localeCompare(a.lastActiveAt ?? "");
    return (b[sortKey] as number) - (a[sortKey] as number);
  });

  const columns: { key: SortKey; label: string }[] = [
    { key: "views30d", label: "Visninger (30d)" },
    { key: "favorites", label: "Favoritter" },
    { key: "bids", label: "Bud" },
    { key: "auctions", label: "Auksjoner" },
    { key: "wins", label: "Vunnet" },
    { key: "lastActiveAt", label: "Sist aktiv" },
  ];
  const COL_COUNT = columns.length + 2;

  if (loading) return <div className="py-10 text-center text-sm text-muted-foreground">Laster …</div>;
  if (!customers.length) return <div className="py-10 text-center text-sm text-muted-foreground">Ingen kunder.</div>;

  return (
    <Card>
      <CardContent className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-muted-foreground">
              <th className="px-4 py-3">Kunde</th>
              <th className="px-4 py-3">Type</th>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3 cursor-pointer select-none ${sortKey === col.key ? "text-foreground" : ""}`}
                  onClick={() => setSortKey(col.key)}
                >
                  {col.label} {sortKey === col.key ? "▼" : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((c) => {
              const meta = CUSTOMER_META[c.type];
              const isOpen = openId === c.organizationId;
              return (
                <Fragment key={c.organizationId}>
                  <tr
                    className="cursor-pointer border-t border-border hover:bg-muted/40"
                    onClick={() => openCustomer(c.organizationId)}
                  >
                    <td className="px-4 py-3 font-medium">
                      {c.organizationName}{c.isBot && <BotBadge />}
                      {c.phone && <div className="text-xs text-muted-foreground">{c.phone}</div>}
                    </td>
                    <td className="px-4 py-3"><Badge variant={meta.variant}>{meta.label}</Badge></td>
                    <td className="px-4 py-3">{c.views30d}</td>
                    <td className="px-4 py-3">{c.favorites}</td>
                    <td className="px-4 py-3">{c.bids}</td>
                    <td className="px-4 py-3">{c.auctions}</td>
                    <td className="px-4 py-3">{c.wins}</td>
                    <td className="px-4 py-3 text-muted-foreground">{relativeTime(c.lastActiveAt)}</td>
                  </tr>
                  {isOpen && (
                    <tr className="border-t border-border bg-muted/20">
                      <td colSpan={COL_COUNT} className="px-4 py-3">
                        <CustomerActivityFeed feed={feeds[c.organizationId]} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

export function AktivitetPage() {
  const [hideBots, setHideBots] = useState(false);
  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Activity className="h-6 w-6 text-accent" />
          <h1 className="text-2xl font-bold">Aktivitet</h1>
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={hideBots}
            onChange={(e) => setHideBots(e.target.checked)}
            className="h-4 w-4 rounded border-border accent-accent"
          />
          Skjul bots
        </label>
      </div>
      <Tabs defaultValue="biler">
        <TabsList>
          <TabsTrigger value="biler">Biler</TabsTrigger>
          <TabsTrigger value="kunder">Kunder</TabsTrigger>
        </TabsList>
        <TabsContent value="biler"><BilerTab hideBots={hideBots} /></TabsContent>
        <TabsContent value="kunder"><KunderTab hideBots={hideBots} /></TabsContent>
      </Tabs>
    </div>
  );
}
